import { Link } from 'react-router-dom';

interface FeatureCardProps {
  icon: string;
  title: string;
  description: string;
  link: string;
  gradient: string;
  iconBg: string;
}

function FeatureCard({ icon, title, description, link, gradient, iconBg }: FeatureCardProps) {
  return (
    <Link
      to={link}
      className="group relative bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 overflow-hidden"
    >
      {/* Gradient Background on Hover */}
      <div className={`absolute inset-0 bg-gradient-to-br ${gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-500`}></div>
      
      {/* Icon */}
      <div className="relative mb-6">
        <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${iconBg} flex items-center justify-center shadow-lg group-hover:shadow-xl transition-all duration-300 group-hover:scale-110 group-hover:rotate-3`}>
          <i className={`${icon} text-4xl text-white`}></i>
        </div>
      </div>

      {/* Content */}
      <div className="relative">
        <h3 className="text-2xl font-black text-gray-900 mb-3 group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:bg-clip-text group-hover:from-orange-600 group-hover:to-rose-600 transition-all duration-300">
          {title}
        </h3>
        <p className="text-gray-600 leading-relaxed mb-4">
          {description}
        </p>
        <div className="flex items-center gap-2 text-orange-600 font-bold group-hover:gap-4 transition-all duration-300">
          <span>Explore Now</span>
          <i className="ri-arrow-right-line text-xl group-hover:translate-x-1 transition-transform duration-300"></i>
        </div>
      </div>

      {/* Decorative Corner */}
      <div className={`absolute -top-8 -right-8 w-24 h-24 bg-gradient-to-br ${gradient} rounded-full opacity-10 group-hover:opacity-20 transition-opacity duration-500`}></div>
    </Link>
  );
}

export default function WhyMeowCentral() {
  return (
    <section className="py-20 bg-gradient-to-b from-white to-orange-50">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-100 rounded-full mb-4">
            <i className="ri-star-fill text-orange-500"></i>
            <span className="text-sm font-bold text-orange-700">Why Choose Us</span>
          </div>
          <h2 className="text-5xl md:text-6xl font-black text-gray-900 mb-4">
            Everything Cats,
            <br />
            <span className="bg-gradient-to-r from-orange-600 to-rose-600 bg-clip-text text-transparent">
              All in One Place
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            From entertainment to adoption to shopping — we're your complete cat companion
          </p>
        </div>

        {/* Feature Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          <FeatureCard
            icon="ri-play-circle-fill"
            title="Endless Videos"
            description="Watch thousands of funny, cute, and educational cat videos curated daily by our community"
            link="/videos"
            gradient="from-orange-500 to-rose-500"
            iconBg="from-orange-500 to-rose-500"
          />
          <FeatureCard
            icon="ri-heart-fill"
            title="Adopt a Friend"
            description="Find your perfect feline companion from verified shelters and rescue organizations nationwide"
            link="/adopt"
            gradient="from-rose-500 to-pink-500"
            iconBg="from-rose-500 to-pink-500"
          />
          <FeatureCard
            icon="ri-shopping-bag-fill"
            title="Premium Shop"
            description="Discover top-rated cat products, toys, food, and accessories with exclusive deals and fast shipping"
            link="/shop"
            gradient="from-amber-500 to-orange-500"
            iconBg="from-amber-500 to-orange-500"
          />
          <FeatureCard
            icon="ri-book-open-fill"
            title="Expert Guides"
            description="Learn from veterinarians and cat behaviorists with our comprehensive care guides and breed info"
            link="/cat-care-guide"
            gradient="from-pink-500 to-rose-500"
            iconBg="from-pink-500 to-rose-500"
          />
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 text-center">
          <p className="text-gray-600 mb-6 text-lg">
            Join over <span className="font-black text-orange-600">1 million cat lovers</span> who trust Meow Central
          </p>
          <div className="flex items-center justify-center gap-4">
            <div className="flex -space-x-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="w-12 h-12 rounded-full bg-gradient-to-br from-orange-400 to-rose-400 border-4 border-white shadow-lg"></div>
              ))}
            </div>
            <div className="flex items-center gap-1 text-amber-500">
              {[1, 2, 3, 4, 5].map((i) => (
                <i key={i} className="ri-star-fill text-xl"></i>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
